// components/use-case-details/use-case-technical-requirements.tsx
"use client";

import { ReactNode } from "react";

// Import the ProcessTechTerms component
import { ProcessTechTerms } from "@/components/common/tech-term-system";

// v2

const DifficultyBadge = ({ level }: { level: "easy" | "medium" | "hard" }) => {
  const colors = {
    easy: "bg-green-600",
    medium: "bg-yellow-600",
    hard: "bg-red-600",
  };

  return (
    <span
      className={`text-xs font-semibold px-2 py-1 rounded-full ${colors[level]} text-white ml-2`}
    >
      {level.charAt(0).toUpperCase() + level.slice(1)}
    </span>
  );
};

// Enhanced requirement item with tooltips and context
export const RequirementItem = ({
  text,
  difficulty,
  contextNote,
}: {
  text: string;
  difficulty?: "easy" | "medium" | "hard";
  contextNote?: string;
}) => (
  <div className="mb-3">
    <div className="flex items-start">
      <span className="text-blue-400 mr-2">→</span>
      <div>
        <div className="flex items-center">
          <ProcessTechTerms text={text} />
          {difficulty && <DifficultyBadge level={difficulty} />}
        </div>
        {contextNote && (
          <p className="text-gray-400 text-sm mt-1 ml-1">{contextNote}</p>
        )}
      </div>
    </div>
  </div>
);

// v1
interface TechRequirementsCardProps {
  title: string;
  description: string;
  items: string[];
  icon?: ReactNode;
  variant?: "data" | "tech" | "time" | "team";
  examples?: Record<string, string[]>; // Add examples
}

export function TechRequirementsCard({ 
  title, 
  description, 
  items, 
  icon,
  variant = "data",
  examples = {} // Default to empty object
}: TechRequirementsCardProps) {
  // Different colors for each variant
  const variantStyles = {
    data: {
      border: "border-blue-400",
      icon: "text-blue-400",
      bg: "bg-[#1E2735]"
    },
    tech: {
      border: "border-teal",
      icon: "text-teal",
      bg: "bg-[#1E2D30]"
    },
    time: {
      border: "border-orange",
      icon: "text-orange",
      bg: "bg-[#2D2A26]"
    },
    team: {
      border: "border-purple-400",
      icon: "text-purple-400",
      bg: "bg-[#2A2A31]"
    }
  };

  const style = variantStyles[variant];

  return (
    <div className={`${style.bg} rounded-xl p-6 border-t-2 ${style.border}`}>
      <div className="flex items-center gap-3 mb-4">
        {icon && (
          <div className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-black/30 ${style.icon}`}>
            {icon}
          </div>
        )}
        <h3 className="text-xl font-display font-bold text-white">{title}</h3>
      </div>
      
      <p className="text-gray-300 mb-4">{description}</p>
      
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-start mb-2">
            <span className={`${style.icon} mr-2`}>→</span>
            <span className="text-gray-300">{item}</span>
          </li>
          
        ))}

        {Object.keys(examples).map((key) => (
           <div key={key} className="mt-2">
              
              <div  className="mb-2">
                <span className="text-teal mr-2" />
                <span className="text-sm text-light-gray">{key}: </span>
                {examples[key].map((example, ex_index) => (
                    <span key={ex_index} className="text-sm italic text-gray-400">
                      {ex_index > 0 ? ', ' : ''}{example}
                    </span>
                ))}
               </div>
           </div>
        ))}
      </ul>
    </div>
  );
}

interface TechnicalRequirementsProps {
  children: ReactNode;
}

export function TechnicalRequirements({ children }: TechnicalRequirementsProps) {
  return (
    <div className="my-12">
      <h2 className="text-3xl font-display font-bold text-white mb-6">Technical Requirements</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {children}
      </div>
    </div>
  );
}

// Pre-configured components with icons (updated with examples)
export function DataMaturityCard({ 
  title = "Data Requirements", 
  description = "To implement this solution, you'll need:", 
  items,
  examples = {}
}: { title?: string; description?: string; items: string[]; examples?: Record<string, string[]> }) {
  const DataIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 8C8.13 8 5 9.5 5 11.5V17.5C5 19.5 8.13 21 12 21C15.87 21 19 19.5 19 17.5V11.5C19 9.5 15.87 8 12 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 14.5C5 16.5 8.13 18 12 18C15.87 18 19 16.5 19 14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 11.5C5 13.5 8.13 15 12 15C15.87 15 19 13.5 19 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 8C15.87 8 19 6.5 19 4.5C19 2.5 15.87 1 12 1C8.13 1 5 2.5 5 4.5C5 6.5 8.13 8 12 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  
  return (
    <TechRequirementsCard
      title={title}
      description={description}
      items={items}
      icon={<DataIcon />}
      variant="data"
      examples={examples}
    />
  );
}

export function TechStackCard({ 
  title = "Technology Stack", 
  description = "Core technologies used:", 
  items,
  examples = {}
}: { title?: string; description?: string; items: string[]; examples?: Record<string, string[]> }) {
  const TechIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 5V19C3 21 4 22 6 22H18C20 22 21 21 21 19V5C21 3 20 2 18 2H6C4 2 3 3 3 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 10H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 15H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  
  return (
    <TechRequirementsCard
      title={title}
      description={description}
      items={items}
      icon={<TechIcon />}
      variant="tech"
      examples={examples}
    />
  );
}

export function TimelineCard({ 
  title = "Implementation Timeline", 
  description = "Expected timeline for deployment:", 
  items,
  examples = {}
}: { title?: string; description?: string; items: string[]; examples?: Record<string, string[]> }) {
  const TimeIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 6V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 12H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  
  return (
    <TechRequirementsCard
      title={title}
      description={description}
      items={items}
      icon={<TimeIcon />}
      variant="time"
      examples={examples}
    />
  );
}

export function TeamCard({ 
  title = "Team Requirements", 
  description = "Required team members and skills:", 
  items,
    examples = {}
}: { title?: string; description?: string; items: string[]; examples?: Record<string, string[]> }) {
  const TeamIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 7.16C17.94 7.15 17.87 7.15 17.81 7.16C16.43 7.11 15.33 5.98 15.33 4.58C15.33 3.15 16.48 2 17.91 2C19.34 2 20.49 3.16 20.49 4.58C20.48 5.98 19.38 7.11 18 7.16Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16.97 14.44C18.34 14.67 19.85 14.43 20.91 13.72C22.32 12.78 22.32 11.24 20.91 10.3C19.84 9.59 18.31 9.35 16.94 9.59" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.97 7.16C6.03 7.15 6.1 7.15 6.16 7.16C7.54 7.11 8.64 5.98 8.64 4.58C8.64 3.15 7.49 2 6.06 2C4.63 2 3.48 3.16 3.48 4.58C3.49 5.98 4.59 7.11 5.97 7.16Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 14.44C5.63 14.67 4.12 14.43 3.06 13.72C1.65 12.78 1.65 11.24 3.06 10.3C4.13 9.59 5.66 9.35 7.03 9.59" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 14.63C11.94 14.62 11.87 14.62 11.81 14.63C10.43 14.58 9.33 13.45 9.33 12.05C9.33 10.62 10.48 9.47 11.91 9.47C13.34 9.47 14.49 10.63 14.49 12.05C14.48 13.45 13.38 14.59 12 14.63Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.09 17.78C7.68 18.72 7.68 20.26 9.09 21.2C10.69 22.27 13.31 22.27 14.91 21.2C16.32 20.26 16.32 18.72 14.91 17.78C13.32 16.72 10.69 16.72 9.09 17.78Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
  
  return (
    <TechRequirementsCard
      title={title}
      description={description}
      items={items}
      icon={<TeamIcon />}
      variant="team"
      examples={examples}
    />
  );
}